import { useEffect, useState } from "react";
import { api, formatMAD, withAdminAuth, resolveMediaUrl } from "@/lib/api";
import { LogOut, Package, ShoppingBag, TrendingUp, Plus, Trash2, Edit3, Upload, X, Copy, Check, Phone, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const STATUS_LABELS = {
    pending: "En attente",
    confirmed: "Confirmée",
    shipped: "Expédiée",
    delivered: "Livrée",
    cancelled: "Annulée",
};

const STORAGE_KEY = "makerizk_admin_passcode";

export default function Admin() {
    const [passcode, setPasscode] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
    const [authenticated, setAuthenticated] = useState(false);
    const [tab, setTab] = useState("orders");
    const [showCode, setShowCode] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (passcode) tryAuth(passcode, { silent: true });
        // eslint-disable-next-line
    }, []);

    const tryAuth = async (code, { silent = false } = {}) => {
        const clean = (code || "").trim();
        if (!clean) {
            if (!silent) setLoginError("Entre ton code d'accès");
            return;
        }
        setLoading(true);
        setLoginError("");
        try {
            await api.post("/admin/verify", {}, withAdminAuth(clean));
            localStorage.setItem(STORAGE_KEY, clean);
            setPasscode(clean);
            setAuthenticated(true);
        } catch (_) {
            localStorage.removeItem(STORAGE_KEY);
            setAuthenticated(false);
            if (!silent) setLoginError("Code invalide. Vérifie majuscules, minuscules et caractères spéciaux.");
        } finally {
            setLoading(false);
        }
    };

    const handleLogin = (e) => {
        e.preventDefault();
        tryAuth(passcode);
    };

    const handleLogout = () => {
        localStorage.removeItem(STORAGE_KEY);
        setPasscode("");
        setAuthenticated(false);
        setLoginError("");
    };

    if (!authenticated) {
        return (
            <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-5">
                <form
                    onSubmit={handleLogin}
                    className="border border-white/10 p-8 w-full max-w-md"
                    data-testid="admin-login-form"
                >
                    <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-3">Make Rizk</p>
                    <h1 className="font-display text-3xl font-black uppercase tracking-tighter mb-8">
                        Admin
                    </h1>
                    <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                        Code d'accès
                    </label>
                    <div className="relative">
                        <input
                            type={showCode ? "text" : "password"}
                            value={passcode}
                            onChange={(e) => {
                                setPasscode(e.target.value);
                                setLoginError("");
                            }}
                            autoComplete="current-password"
                            autoCapitalize="off"
                            autoCorrect="off"
                            spellCheck="false"
                            className="w-full h-12 bg-transparent border border-white/20 focus:border-white text-white text-sm px-4 pe-12 outline-none"
                            data-testid="admin-passcode-input"
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={() => setShowCode((v) => !v)}
                            className="absolute inset-y-0 end-0 flex items-center px-3 text-white/50 hover:text-white transition-colors"
                            aria-label={showCode ? "Masquer" : "Afficher"}
                            data-testid="admin-passcode-toggle"
                        >
                            {showCode ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>
                    {loginError && (
                        <p
                            className="mt-3 text-xs uppercase tracking-[0.15em] text-red-400"
                            data-testid="admin-login-error"
                        >
                            {loginError}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        className="mt-6 w-full h-12 bg-white text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-white/90 transition-colors disabled:opacity-60"
                        data-testid="admin-login-btn"
                    >
                        {loading ? "..." : "Accéder au tableau de bord"}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10" data-testid="admin-page">
            <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-2">
                        Tableau de bord
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
                        Admin
                    </h1>
                </div>
                <button
                    onClick={handleLogout}
                    className="inline-flex items-center gap-2 border border-white/20 px-4 h-10 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white hover:text-black transition-colors"
                    data-testid="admin-logout-btn"
                >
                    <LogOut size={14} /> Déconnexion
                </button>
            </div>

            <Stats passcode={passcode} />

            <div className="flex gap-2 mt-10 border-b border-white/10">
                {[
                    { key: "orders", label: "Commandes" },
                    { key: "products", label: "Produits" },
                ].map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        className={`px-5 py-3 text-xs uppercase tracking-[0.2em] font-semibold border-b-2 transition-colors -mb-px ${
                            tab === t.key
                                ? "border-white text-white"
                                : "border-transparent text-white/50 hover:text-white"
                        }`}
                        data-testid={`admin-tab-${t.key}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="mt-8">
                {tab === "orders" ? <OrdersPanel passcode={passcode} /> : <ProductsPanel passcode={passcode} />}
            </div>
        </div>
    );
}

function Stats({ passcode }) {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats", withAdminAuth(passcode)).then((r) => setStats(r.data));
    }, [passcode]);

    if (!stats) return null;

    const cards = [
        { icon: ShoppingBag, label: "Commandes totales", value: stats.total_orders },
        { icon: Package, label: "En attente", value: stats.pending_orders },
        { icon: TrendingUp, label: "Chiffre d'affaires", value: formatMAD(stats.revenue) },
        { icon: Package, label: "Produits", value: stats.total_products },
    ];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map((c) => {
                const Icon = c.icon;
                return (
                    <div key={c.label} className="border border-white/10 p-5" data-testid={`stat-${c.label}`}>
                        <div className="flex items-center gap-2 text-white/50 mb-3">
                            <Icon size={14} strokeWidth={1.5} />
                            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold">{c.label}</p>
                        </div>
                        <p className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
                            {c.value}
                        </p>
                    </div>
                );
            })}
        </div>
    );
}

function OrdersPanel({ passcode }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/orders", withAdminAuth(passcode))
            .then((r) => setOrders(r.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [passcode]);

    const updateStatus = async (orderId, newStatus) => {
        try {
            await api.patch(
                `/admin/orders/${orderId}/status`,
                { status: newStatus },
                withAdminAuth(passcode)
            );
            toast.success("Statut mis à jour");
            // Update selected order in place if open
            setOrders((prev) =>
                prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
            );
            setSelected((prev) =>
                prev && prev.id === orderId ? { ...prev, status: newStatus } : prev
            );
        } catch (_) {
            toast.error("Erreur mise à jour");
        }
    };

    if (loading) return <p className="text-white/50">Chargement...</p>;
    if (orders.length === 0)
        return <p className="text-white/50 py-10 text-center">Aucune commande pour l'instant.</p>;

    return (
        <>
            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="orders-table">
                    <thead className="text-xs uppercase tracking-[0.2em] text-white/40 border-b border-white/10">
                        <tr>
                            <th className="text-left py-3 pr-3">N°</th>
                            <th className="text-left py-3 pr-3">Client</th>
                            <th className="text-left py-3 pr-3 hidden md:table-cell">Ville</th>
                            <th className="text-left py-3 pr-3 hidden lg:table-cell">Articles</th>
                            <th className="text-right py-3 pr-3">Total</th>
                            <th className="text-left py-3 pr-3">Statut</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((o) => (
                            <tr
                                key={o.id}
                                onClick={() => setSelected(o)}
                                className="border-b border-white/10 cursor-pointer hover:bg-white/[0.03] transition-colors"
                                data-testid={`order-row-${o.order_number}`}
                            >
                                <td className="py-4 pr-3 font-mono text-xs">{o.order_number}</td>
                                <td className="py-4 pr-3">
                                    <p className="font-semibold">{o.customer.name}</p>
                                    <p className="text-white/50 text-xs">{o.customer.phone}</p>
                                </td>
                                <td className="py-4 pr-3 hidden md:table-cell text-white/70">{o.customer.city}</td>
                                <td className="py-4 pr-3 hidden lg:table-cell text-white/70">
                                    {o.items.reduce((s, i) => s + i.quantity, 0)} article(s)
                                </td>
                                <td className="py-4 pr-3 text-right font-semibold">{formatMAD(o.total)}</td>
                                <td className="py-4 pr-3" onClick={(e) => e.stopPropagation()}>
                                    <select
                                        value={o.status}
                                        onChange={(e) => updateStatus(o.id, e.target.value)}
                                        className="bg-transparent border border-white/20 focus:border-white text-xs uppercase tracking-[0.1em] px-2 py-1 outline-none"
                                        data-testid={`order-status-${o.order_number}`}
                                    >
                                        {STATUSES.map((s) => (
                                            <option key={s} value={s} className="bg-black">
                                                {STATUS_LABELS[s]}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selected && (
                <OrderDetailModal
                    order={selected}
                    onClose={() => setSelected(null)}
                    onStatusChange={(s) => updateStatus(selected.id, s)}
                    passcode={passcode}
                    onUpdated={(updated) => {
                        setOrders((prev) =>
                            prev.map((o) => (o.id === updated.id ? updated : o))
                        );
                        setSelected(updated);
                    }}
                />
            )}
        </>
    );
}

function OrderDetailModal({ order, onClose, onStatusChange, passcode, onUpdated }) {
    const [copied, setCopied] = useState(false);
    const [trackingInput, setTrackingInput] = useState(order.tracking_link || "");
    const [savingTracking, setSavingTracking] = useState(false);

    const saveTracking = async () => {
        setSavingTracking(true);
        try {
            const res = await api.patch(
                `/admin/orders/${order.id}`,
                { tracking_link: trackingInput.trim() },
                withAdminAuth(passcode)
            );
            toast.success("Lien de suivi enregistré");
            if (onUpdated) onUpdated(res.data);
        } catch (_) {
            toast.error("Erreur enregistrement");
        } finally {
            setSavingTracking(false);
        }
    };

    const buildShippingText = () => {
        const lines = [
            `Commande ${order.order_number}`,
            `Nom : ${order.customer.name}`,
            `Téléphone : ${order.customer.phone}`,
            `Adresse : ${order.customer.address}`,
            `Ville : ${order.customer.city}`,
        ];
        if (order.customer.notes) lines.push(`Note : ${order.customer.notes}`);
        lines.push("");
        lines.push("Articles :");
        order.items.forEach((it) => {
            const size = it.size ? ` · Taille ${it.size}` : "";
            lines.push(`  - ${it.quantity}× ${it.name}${size}`);
        });
        lines.push("");
        lines.push(`Montant à encaisser (COD) : ${formatMAD(order.total)}`);
        return lines.join("\n");
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(buildShippingText());
            setCopied(true);
            toast.success("Infos copiées");
            setTimeout(() => setCopied(false), 2500);
        } catch (_) {
            toast.error("Copie impossible");
        }
    };

    const date = new Date(order.created_at).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="order-detail-modal"
        >
            <div
                className="relative bg-[#0a0a0a] border border-white/10 w-full max-w-2xl my-8"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 end-4 text-white/60 hover:text-white transition-colors"
                    aria-label="Fermer"
                    data-testid="order-detail-close"
                >
                    <X size={20} strokeWidth={1.5} />
                </button>

                <div className="p-6 sm:p-8 border-b border-white/10">
                    <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-2">
                        Commande · {date}
                    </p>
                    <p className="font-display text-3xl font-black tracking-tighter uppercase" data-testid="detail-order-number">
                        {order.order_number}
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                        <span className="text-xs uppercase tracking-[0.2em] text-white/40">Statut :</span>
                        <select
                            value={order.status}
                            onChange={(e) => onStatusChange(e.target.value)}
                            className="bg-transparent border border-white/20 focus:border-white text-xs uppercase tracking-[0.1em] px-3 py-1.5 outline-none"
                            data-testid="detail-status-select"
                        >
                            {STATUSES.map((s) => (
                                <option key={s} value={s} className="bg-black">
                                    {STATUS_LABELS[s]}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="p-6 sm:p-8 grid sm:grid-cols-2 gap-6 border-b border-white/10">
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Client</p>
                        <p className="font-semibold" data-testid="detail-customer-name">
                            {order.customer.name}
                        </p>
                        <a
                            href={`tel:${order.customer.phone}`}
                            className="block text-sm text-white/70 hover:text-white mt-1"
                            data-testid="detail-customer-phone"
                        >
                            {order.customer.phone}
                        </a>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Livraison</p>
                        <p className="text-sm text-white/80 whitespace-pre-line" data-testid="detail-address">
                            {order.customer.address}
                        </p>
                        <p className="text-sm text-white/80 mt-1" data-testid="detail-city">
                            {order.customer.city}
                        </p>
                        {order.customer.notes && (
                            <p className="mt-3 text-xs text-white/50 italic" data-testid="detail-notes">
                                Note : {order.customer.notes}
                            </p>
                        )}
                    </div>
                </div>

                <div className="p-6 sm:p-8 border-b border-white/10">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">
                        Articles ({order.items.length})
                    </p>
                    <div className="space-y-3" data-testid="detail-items">
                        {order.items.map((item, i) => (
                            <div
                                key={i}
                                className="flex gap-3 pb-3 border-b border-white/5 last:border-0 last:pb-0"
                                data-testid={`detail-item-${i}`}
                            >
                                <div className="w-14 h-16 bg-white/5 flex-shrink-0 overflow-hidden">
                                    {item.image && (
                                        <img
                                            src={resolveMediaUrl(item.image)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-sm truncate">{item.name}</p>
                                    <p className="text-xs text-white/50 mt-0.5">
                                        {item.size ? `Taille ${item.size} · ` : ""}Quantité {item.quantity}
                                    </p>
                                    <p className="text-xs text-white/70 mt-0.5">
                                        {formatMAD(item.price)} × {item.quantity}
                                    </p>
                                </div>
                                <p className="text-sm font-semibold whitespace-nowrap">
                                    {formatMAD(item.price * item.quantity)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-6 sm:p-8 space-y-2 text-sm">
                    <div className="flex justify-between text-white/70">
                        <span>Sous-total</span>
                        <span>{formatMAD(order.subtotal)}</span>
                    </div>
                    {order.discount > 0 && (
                        <div className="flex justify-between text-white">
                            <span>
                                Remise{order.promo_code ? ` · ${order.promo_code}` : ""}
                            </span>
                            <span>−{formatMAD(order.discount)}</span>
                        </div>
                    )}
                    <div className="flex justify-between text-white/70">
                        <span>Livraison</span>
                        <span>{formatMAD(order.shipping_fee)}</span>
                    </div>
                    <div className="flex justify-between font-display uppercase font-bold text-lg pt-3 border-t border-white/10 mt-3">
                        <span>Total à encaisser (COD)</span>
                        <span data-testid="detail-total">{formatMAD(order.total)}</span>
                    </div>
                </div>

                {/* Tracking link */}
                <div className="px-6 sm:px-8 pb-6 sm:pb-8">
                    <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                        Lien de suivi (optionnel)
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            value={trackingInput}
                            onChange={(e) => setTrackingInput(e.target.value)}
                            placeholder="Ex: https://tracking.aramex.com/..."
                            className="flex-1 h-11 bg-transparent border border-white/20 focus:border-white text-white text-sm px-3 outline-none"
                            data-testid="detail-tracking-input"
                        />
                        <button
                            type="button"
                            onClick={saveTracking}
                            disabled={savingTracking || trackingInput === (order.tracking_link || "")}
                            className="h-11 px-4 border border-white/30 hover:bg-white hover:text-black text-xs uppercase tracking-[0.2em] font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="detail-tracking-save"
                        >
                            {savingTracking ? "..." : "Enregistrer"}
                        </button>
                    </div>
                    <p className="mt-2 text-xs text-white/40">
                        Ce lien sera visible par le client sur sa page de confirmation.
                    </p>
                </div>

                <div className="p-6 sm:p-8 border-t border-white/10 flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={handleCopy}
                        className={`flex-1 inline-flex items-center justify-center gap-2 h-12 text-xs uppercase tracking-[0.25em] font-bold transition-colors ${
                            copied
                                ? "bg-white text-black"
                                : "bg-white text-black hover:bg-white/90"
                        }`}
                        data-testid="detail-copy-btn"
                    >
                        {copied ? (
                            <>
                                <Check size={14} strokeWidth={2} /> Copié !
                            </>
                        ) : (
                            <>
                                <Copy size={14} strokeWidth={1.5} /> Copier les infos de livraison
                            </>
                        )}
                    </button>
                    <a
                        href={`tel:${order.customer.phone}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 h-12 border border-white/30 text-white text-xs uppercase tracking-[0.25em] font-bold hover:bg-white hover:text-black transition-colors"
                        data-testid="detail-call-btn"
                    >
                        <Phone size={14} strokeWidth={1.5} /> Appeler le client
                    </a>
                </div>
            </div>
        </div>
    );
}

const EMPTY_PRODUCT = {
    name: "",
    category: "tshirt",
    price: 0,
    description: "",
    images: [],
    sizes: ["S", "M", "L", "XL"],
    stock: 50,
    new_arrival: false,
    bestseller: false,
};

function ProductsPanel({ passcode }) {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // product or EMPTY_PRODUCT (creating)

    const load = () => {
        setLoading(true);
        api.get("/products")
            .then((r) => setProducts(r.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const remove = async (id) => {
        if (!window.confirm("Supprimer ce produit ?")) return;
        try {
            await api.delete(`/products/${id}`, withAdminAuth(passcode));
            toast.success("Produit supprimé");
            load();
        } catch (_) {
            toast.error("Erreur suppression");
        }
    };

    const save = async (data) => {
        try {
            const payload = {
                ...data,
                price: parseInt(data.price, 10) || 0,
                stock: parseInt(data.stock, 10) || 0,
                images: Array.isArray(data.images) ? data.images : [],
                sizes: data.category === "cap" ? [] : data.sizes,
            };
            if (data.id) {
                await api.patch(`/products/${data.id}`, payload, withAdminAuth(passcode));
                toast.success("Produit mis à jour");
            } else {
                await api.post("/products", payload, withAdminAuth(passcode));
                toast.success("Produit créé");
            }
            setEditing(null);
            load();
        } catch (_) {
            toast.error("Erreur sauvegarde");
        }
    };

    if (loading) return <p className="text-white/50">Chargement...</p>;

    return (
        <>
            <div className="flex justify-end mb-6">
                <button
                    onClick={() => setEditing({ ...EMPTY_PRODUCT })}
                    className="inline-flex items-center gap-2 bg-white text-black px-4 h-10 text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/90 transition-colors"
                    data-testid="admin-new-product-btn"
                >
                    <Plus size={14} /> Nouveau produit
                </button>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="products-table">
                    <thead className="text-xs uppercase tracking-[0.2em] text-white/40 border-b border-white/10">
                        <tr>
                            <th className="text-left py-3 pr-3">Produit</th>
                            <th className="text-left py-3 pr-3 hidden md:table-cell">Catégorie</th>
                            <th className="text-right py-3 pr-3">Prix</th>
                            <th className="text-right py-3 pr-3 hidden md:table-cell">Stock</th>
                            <th className="text-left py-3 pr-3 hidden lg:table-cell">Flags</th>
                            <th className="text-right py-3 pr-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p) => (
                            <tr key={p.id} className="border-b border-white/10">
                                <td className="py-3 pr-3 flex items-center gap-3">
                                    {p.images?.[0] && (
                                        <img
                                            src={resolveMediaUrl(p.images[0])}
                                            alt=""
                                            className="w-10 h-12 object-cover"
                                        />
                                    )}
                                    <span className="font-semibold">{p.name}</span>
                                </td>
                                <td className="py-3 pr-3 hidden md:table-cell text-white/70">
                                    {p.category === "tshirt" ? "T-Shirt" : "Casquette"}
                                </td>
                                <td className="py-3 pr-3 text-right">{formatMAD(p.price)}</td>
                                <td className="py-3 pr-3 text-right hidden md:table-cell">{p.stock}</td>
                                <td className="py-3 pr-3 hidden lg:table-cell text-xs">
                                    {p.new_arrival && (
                                        <span className="mr-2 px-2 py-0.5 bg-white text-black">NEW</span>
                                    )}
                                    {p.bestseller && (
                                        <span className="px-2 py-0.5 border border-white">BEST</span>
                                    )}
                                </td>
                                <td className="py-3 pr-3 text-right space-x-1">
                                    <button
                                        onClick={() => setEditing(p)}
                                        className="p-2 hover:bg-white/10 transition-colors"
                                        data-testid={`edit-product-${p.id}`}
                                    >
                                        <Edit3 size={14} />
                                    </button>
                                    <button
                                        onClick={() => remove(p.id)}
                                        className="p-2 hover:bg-white/10 transition-colors"
                                        data-testid={`delete-product-${p.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <ProductEditor
                    product={editing}
                    onCancel={() => setEditing(null)}
                    onSave={save}
                    passcode={passcode}
                />
            )}
        </>
    );
}

function ProductEditor({ product, onCancel, onSave, passcode }) {
    const [form, setForm] = useState({
        ...product,
        images: Array.isArray(product.images) ? [...product.images] : [],
    });
    const [uploading, setUploading] = useState(false);
    const set = (k, v) => setForm({ ...form, [k]: v });

    const handleFiles = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const file of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", file);
                const res = await api.post("/upload", fd, {
                    headers: {
                        "X-Admin-Passcode": passcode,
                        "Content-Type": "multipart/form-data",
                    },
                });
                uploaded.push(res.data.url);
            }
            setForm((f) => ({ ...f, images: [...(f.images || []), ...uploaded] }));
            toast.success(`${uploaded.length} image(s) uploadée(s)`);
        } catch (e) {
            toast.error("Erreur upload: " + (e.response?.data?.detail || e.message));
        } finally {
            setUploading(false);
        }
    };

    const removeImage = (idx) => {
        setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="product-editor-modal">
            <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 sm:p-8">
                <p className="font-display text-2xl font-black uppercase tracking-tighter mb-6">
                    {product.id ? "Modifier le produit" : "Nouveau produit"}
                </p>
                <div className="space-y-4">
                    <TxtField label="Nom" value={form.name} onChange={(v) => set("name", v)} testid="edit-name" />
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                                Catégorie
                            </label>
                            <select
                                value={form.category}
                                onChange={(e) => set("category", e.target.value)}
                                className="w-full h-12 bg-transparent border border-white/20 text-white text-sm px-4 outline-none"
                                data-testid="edit-category"
                            >
                                <option value="tshirt" className="bg-black">
                                    T-Shirt
                                </option>
                                <option value="cap" className="bg-black">
                                    Casquette
                                </option>
                            </select>
                        </div>
                        <TxtField
                            label="Prix (MAD)"
                            type="number"
                            value={form.price}
                            onChange={(v) => set("price", v)}
                            testid="edit-price"
                        />
                    </div>
                    <TxtField
                        label="Stock"
                        type="number"
                        value={form.stock}
                        onChange={(v) => set("stock", v)}
                        testid="edit-stock"
                    />
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                            Description
                        </label>
                        <textarea
                            rows={3}
                            value={form.description}
                            onChange={(e) => set("description", e.target.value)}
                            className="w-full bg-transparent border border-white/20 text-white text-sm px-4 py-3 outline-none resize-none"
                            data-testid="edit-description"
                        />
                    </div>

                    {/* Image uploader */}
                    <div>
                        <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                            Photos ({form.images.length})
                        </label>
                        <label
                            className={`flex flex-col items-center justify-center gap-2 border border-dashed border-white/25 py-8 cursor-pointer hover:border-white transition-colors ${
                                uploading ? "opacity-50 pointer-events-none" : ""
                            }`}
                            data-testid="edit-upload-dropzone"
                        >
                            <Upload size={18} strokeWidth={1.5} />
                            <span className="text-xs uppercase tracking-[0.2em] font-semibold">
                                {uploading ? "Envoi..." : "Cliquer pour choisir des photos"}
                            </span>
                            <span className="text-[10px] text-white/40">
                                JPG, PNG, WEBP · 8 Mo max par fichier
                            </span>
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                multiple
                                onChange={(e) => handleFiles(e.target.files)}
                                className="hidden"
                                data-testid="edit-upload-input"
                            />
                        </label>

                        {form.images.length > 0 && (
                            <div className="mt-3 grid grid-cols-4 gap-2">
                                {form.images.map((img, i) => (
                                    <div
                                        key={i}
                                        className="relative aspect-square bg-[#0f0f0f] overflow-hidden group"
                                        data-testid={`edit-image-${i}`}
                                    >
                                        <img
                                            src={resolveMediaUrl(img)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeImage(i)}
                                            className="absolute top-1 right-1 w-6 h-6 bg-black/80 hover:bg-white hover:text-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                            data-testid={`edit-image-remove-${i}`}
                                            aria-label="Supprimer image"
                                        >
                                            <X size={12} />
                                        </button>
                                        {i === 0 && (
                                            <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-[0.1em] bg-white text-black px-1.5 py-0.5">
                                                Principal
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex gap-6 pt-2">
                        <Checkbox
                            label="Nouveauté"
                            checked={form.new_arrival}
                            onChange={(v) => set("new_arrival", v)}
                            testid="edit-new"
                        />
                        <Checkbox
                            label="Best-seller"
                            checked={form.bestseller}
                            onChange={(v) => set("bestseller", v)}
                            testid="edit-best"
                        />
                    </div>
                </div>
                <div className="mt-8 flex gap-3">
                    <button
                        onClick={onCancel}
                        className="flex-1 h-12 border border-white/20 text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/10 transition-colors"
                        data-testid="edit-cancel"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={() => onSave(form)}
                        className="flex-1 h-12 bg-white text-black text-xs uppercase tracking-[0.2em] font-bold hover:bg-white/90 transition-colors"
                        data-testid="edit-save"
                    >
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}

function TxtField({ label, value, onChange, testid, type = "text" }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 font-semibold block mb-2">
                {label}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-12 bg-transparent border border-white/20 focus:border-white text-white text-sm px-4 outline-none transition-colors"
                data-testid={testid}
            />
        </div>
    );
}

function Checkbox({ label, checked, onChange, testid }) {
    return (
        <label className="flex items-center gap-3 cursor-pointer select-none">
            <span
                onClick={() => onChange(!checked)}
                className={`w-5 h-5 border flex items-center justify-center ${
                    checked ? "bg-white border-white" : "border-white/30"
                }`}
                data-testid={testid}
            >
                {checked && <span className="text-black text-xs font-bold">✓</span>}
            </span>
            <span className="text-sm">{label}</span>
        </label>
    );
}
